export interface IHolderCommon {
  profile: IHolderProfile
  contact: IHolderContact
  /** 認購聲明 */
  kyc: IHolderStatements
  /** 本次交易更動的欄位: POST to server only */
  changed_fields?: string[]
  /** 由後端提供 */
  w8_expired?: boolean
  w8_valid_year?: number | null
  /** 當前帳戶 account 關聯的共同持有人，不含自己目前帳戶本身 */
  identity_relations?: string[]
}

export class Shareholder implements IHolderCommon {
  profile: IHolderProfile
  contact: IHolderContact
  kyc: IHolderStatements
  changed_fields?: string[]
  w8_expired?: boolean
  w8_valid_year?: number | null
  identity_relations?: string[]

  constructor(holder: IHolderCommon) {
    this.profile = holder.profile
    this.contact = holder.contact
    this.kyc = holder.kyc
    this.changed_fields = holder.changed_fields
    this.w8_expired = holder.w8_expired
    this.w8_valid_year = holder.w8_valid_year
    this.identity_relations = holder.identity_relations
  }
}

export class Holder extends Shareholder {
  constructor(holder: IHolderCommon) {
    super(holder)
  }
}

/**
 * @deprecated `bank` 已移至 account，無法再由 holder 結構可靠判斷。請改用 `holder === account.holder` 或 `holderIndex === 0`。
 */
export function isHolder(holder: any): holder is Holder {
  return holder instanceof Holder || holder?.bank !== undefined
}

/**
 * @deprecated `bank` 已移至 account，無法再由 holder 結構可靠判斷。請改用 `account.shareholders.includes(holder)` 或 `holderIndex > 0`。
 */
export function isShareholder(holder: any): holder is Shareholder {
  return holder instanceof Shareholder || holder?.bank === undefined
}

/**
 * 依檔案／表單 holder index 取得主認購人或共同持有人。
 * index 0 固定保留給帳戶層級與主認購人；index 1..N 對應 shareholders 0..N-1。
 * UG 聯名戶的自然人資料也固定使用 index 0，不建立 shareholders。
 */
export function getAccountHolderByIndex<T>(
  account: { holder?: T | null, shareholders?: T[] | null } | null | undefined,
  holderIndex: number,
): T | undefined {
  if (!account) return undefined
  if (holderIndex === 0) return account.holder ?? undefined
  return account.shareholders?.[holderIndex - 1]
}

interface IHolderProfileBusiness {
  /** 公司名稱* */
  business_name: string
  /** 公司註冊地* */
  business_location: string
  /** 公司登記證號碼* */
  business_id_number: string
}

interface IHolderProfilePersonal {
  /** 認購者名稱* */
  personal_name: IName
  /** 出生地* */
  birthplace: string
  /** 
   * 出生日期
   * @format YYYY-MM-DD
   */
  date_of_birth: string
  /** 
   * 國籍
   * @format [Code], [Country Name]
   */
  nationality: string
  /** 
   * 護照簽發地
   * @format [Code], [Country Name]
   */
  passport_issuing_place: string
  /** 護照號碼* */
  passport_number: string
  /** 
   * 第二國籍（如適用
   * @format [Code], [Country Name]
   */
  second_nationality: string
  /** 
   * 第二稅務繳納地（如適用）
   * @format [Code], [Country Name]
   */
  second_tax_payment_location: string

  /** 職業* */
  occupation: string

  /** 
   * 資金來源
   * @options
   *  * Sale of Existing Assets
   *  * Inheritance
   *  * Salary/Bonus
   *  * Other
   */
  source_of_funds: 'Sale of Existing Assets' | 'Inheritance' | 'Salary/Bonus' | 'Other'

  /** 聯名戶彼此關係* */
  shareholder_relationship: string

}

export const InvestorQualifications = ["Entity", "Individual", "Shareholder", "Institution"] as const

export type BeneficiaryCategory = typeof BeneficiaryCategories[number]
export const BeneficiaryCategories = [
  'us_individual',
  'individual',
  'broker',
  'insurance_company',
  'sec_insurance_company',
  'private_fund',
  'non_profit_entity',
  'pension_plan',
  'banking',
  'us_state_entity',
  'us_state_pension_plan',
  'sovereign_wealth_fund',
  'behalf_of_other_foreigner',
  'other'
] as const

/** 聲明事項 */
export interface IFormSubscriptionStatement {
  /** 自益投資 */
  is_self_benefit?: boolean;
  /** 中國籍之最終受益人 */
  is_beneficiary_in_china?: boolean;

  /** 最終受益人是否為組合基金 */
  is_fof_beneficiary?: boolean;

  /** 最終受益人 */
  beneficiary?: Array<BeneficiaryCategory>
  beneficiary_sec_insurance_company?: string
  beneficiary_other?: string

  /** 聯名戶 */
  /** 共同參與股份持有人之授權 */
  is_share_holder_authed?: boolean;

  /** 福利計畫投資者 */
  is_benefit_investor?: boolean;
  is_benefit_assets?: boolean;
  benefit_assets_percentage?: string;
  /** FINRA Person status*/
  restricted_person_status?: string;
  covered_person_status?: string;
  /** 結構型商品之提供者 */
  is_structured_product?: boolean;
  /** 投資者資格狀態 */
  investor_qualification?: typeof InvestorQualifications[number];
  entity_minimum_amount?: string;
  individual_minimum_amount?: string;
  shareholder_minimum_amount?: string;
  institution_minimum_amount?: string;
  /** 受限人士 */
  restricted_person_explain?: string | null;
  /** 覆蓋人士 */
  covered_person_explain?: string | null;
}

export interface IFormFundSourceProof {
  earnings?: {
    name?: string;
    year?: string;
    industry?: string;
    occupation?: string;
    avg_salary?: string;
    // employee
    employee_name?: string;
    // prior employment
    prior_employment?: string;
  };
  inheritance?: {
    account_name?: string;
    decedent_name?: string;
    relationship?: string;
    source_of_wealth?: string;
  };
  investment_returns?: {
    type?: string;
    account_name?: string;
    bank?: string;
    source_of_wealth?: string;
  };
  others?: {
    name?: string;
    source_of_wealth?: string;
  };
}

export const Citizenships = [
  'citizen',
  'surrendered',
  'foreigner',
] as const

export type Citizenship = typeof Citizenships[number]

export interface ISelfCertification {
  citizenship?: Citizenship
  tin_no?: string
  tax_references?: Array<{
    location?: string
    type?: string
    no?: string
    no_tin_reason?: string
    no_tin_explain?: string
  }>
}

export const SourcesOfFunds = ['earnings', 'inheritance', 'investment_returns', 'others'] as const
export type SourceOfFunds = typeof SourcesOfFunds[number]

export const SubscriberTypes = [
  'individual',
  'estate',
  'stock_exchange',
  'gov_entity',
  'financial_institution',
  'unregulated_fund',
  'corporation',
  'regulated_fund',
  'limited_liability_company',
  'gov_dept',
  'partnership',
  'private_company',
  'general_partnership',
  'educational_institution',
  'trust',
  'foundation',
  'pension_plan',
  'other'
] as const
export type SubscriberType = typeof SubscriberTypes[number]

export const RequiredQuestionnaireKeys = [
  'is_behalf_another',
  'is_individual_in_sensitive_country',
  'is_entities_owner_in_sensitive_country',
  'is_government_in_sensitive_country',
  'is_any_of_sanctions_lists',
  'is_entities_own_by_above',
  'is_revenue_from_sensitive_country',
  'is_involve_weapons',
  'is_politically_exposed_person',
  // // Description
  // 'revenue_from_sensitive_country',
  // 'involve_weapons',
] as const

export type AMLQuestionnaire = {
  // 1
  is_behalf_another?: boolean | null
  // 2-i
  is_individual_in_sensitive_country?: boolean | null
  // 2-ii
  is_entities_owner_in_sensitive_country?: boolean | null
  // 2-iii
  is_government_in_sensitive_country?: boolean | null
  // 2-iv
  is_any_of_sanctions_lists?: boolean | null
  // 2-v
  is_entities_own_by_above?: boolean | null
  // 3
  is_revenue_from_sensitive_country?: boolean | null
  revenue_from_sensitive_country?: string
  // 4
  is_involve_weapons?: boolean | null
  involve_weapons?: string
  // 5
  is_politically_exposed_person?: boolean | null
}

export type IHolderStatements = {
  /** 
   * 資金來源
   */
  source_of_funds?: Array<SourceOfFunds>;

  /**
   * 資金來源證明
   * Depend on `source_of_funds`
   */
  source_proof?: Partial<IFormFundSourceProof>;

  /**
   * 認購者類型
   */
  subscriber_type?: SubscriberType
  subscriber_other?: string

  /**
   * AML問卷
   */
  questionnaire?: AMLQuestionnaire

  /**
   * 聲明事項 (KYC)
   */
  statement?: IFormSubscriptionStatement;

  /**
   * 自我聲明表
   */
  self_certification?: ISelfCertification
};

export interface IHolderProfile extends IHolderProfilePersonal, IHolderProfileBusiness {
  /** 
   * 稅務繳納地
   * @format [Code], [Country Name]
   */
  tax_payment_location: string
  /** 稅籍號碼 */
  tax_id_number: string | null

  no_tin_reason: string | null
  no_tin_explain: string | null

  /** 
   * 第二稅務繳納地（如適用）
   * @format [Code], [Country Name]
   */
  second_tax_payment_location: string
  /** 第二稅籍號碼 */
  second_tax_id_number: string | null

  second_no_tin_reason: string | null
  second_no_tin_explain: string | null
}

export interface IHolderContact {
  /** 電話號碼* */
  phone_number: IPhoneNumber[]
  /** 傳真號碼 */
  fax_number: IPhoneNumber[]
  /** 電子郵件* */
  email: string[]
  /** 永久居住地址 */
  residence_address: IAddress
  /** 現居地址或公司註冊地址 */
  company_address: IAddress
  /** 郵寄地址 */
  mailing_address: IAddress
}

export interface IHolderBankAccount {
  /** 銀行賬戶：銀行國際代碼* */
  bank_swift_code: string
  /** 銀行賬戶：銀行名稱* */
  bank_name: string
  /** 銀行賬戶：銀行地址 */
  bank_address?: string
  /** 銀行賬戶：銀行帳戶名稱* */
  bank_account_name: string
  /** 銀行賬戶：銀行帳號* */
  bank_account_number: string
  /** 銀行賬戶：備註 */
  bank_remark?: string
  /** 銀行賬戶：中間行名稱 */
  intermediary_bank_name?: string
  /** 銀行賬戶：中間行國際代碼 */
  intermediary_bank_swift_code?: string
  /** 銀行賬戶：收款銀行帳號 */
  beneficiary_bank_account_number?: string
  /** 銀行賬戶：第二中間行帳戶名稱 */
  second_intermediary_bank_account_name?: string
  /** 銀行賬戶：第二中間行帳號 */
  second_intermediary_bank_account_number?: string
  /** 銀行賬戶：帳戶類別* */
  bank_account_type: 'Joint Account' | 'Individual Account'
  /** 此銀行帳戶名稱與認購人名稱及人數完全相符 * */
  bank_joint_data_match?: boolean
  /** 變更銀行帳戶資料* */
  /** 
   * 變更銀行帳戶資料
   * * true 套用到後續交易
   * * false 限本次交易
   */
  bank_account_change_update?: boolean,
  /** 本人/我等確認上述銀行帳戶資料之完整性與正確性 */
  bank_account_user_confirmed: boolean
}

export interface IName {
  /** 名字 */
  first_name: string
  /** 姓氏 */
  last_name: string
  /** 中間名(非必填) */
  middle_name?: string | ''
}

export interface IPhoneNumber {
  /** 國碼 */
  country_code: string | null
  /** 區碼 */
  area_code: string | null
  /** 電話號碼 */
  phone_number: string | null
}

export interface IAddress {
  /** 街區門牌號 */
  street: string | null
  /** 鄉鎮市 (原：城市/區域) */
  city: string | null
  /** 縣/省/州 */
  county: string | null
  /** 郵遞區號 */
  postal_code: string | null
  /** 國家 */
  country: string | null
}
